import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Dialog } from 'primeng/dialog';
import { ButtonDirective } from 'primeng/button';
import { DOCUMENT } from '@angular/common';
import { ProjectFile } from '../../models';

export interface DuplicateRow {
	file: File;
	existing: ProjectFile;
}

export type ResolutionAction = 'replace' | 'new';

export interface DuplicateResolution {
	file: File;
	action: ResolutionAction;
	replaceMediaId?: string;
}

interface RowState {
	row: DuplicateRow;
	action: ResolutionAction;
	extensionMatches: boolean;
}

function fileExtension(name: string): string {
	const dot = name.lastIndexOf('.');
	return dot >= 0 ? name.slice(dot).toLowerCase() : '';
}

function formatBytes(bytes: number): string {
	if (bytes < 1024) return `${bytes} B`;
	const kb = bytes / 1024;
	if (kb < 1024) return `${Math.round(kb)} KB`;
	const mb = kb / 1024;
	if (mb < 1024) return `${mb.toFixed(1)} MB`;
	return `${(mb / 1024).toFixed(1)} GB`;
}

/**
 * Pre-upload prompt for duplicate filenames. The caller computes per-row
 * `{ file, existing }` pairs (filename + category match against the loaded
 * media list) and any non-duplicate passthrough files. The user picks
 * Replace or Upload-as-new for each duplicate; the resolved decisions are
 * emitted on `confirm` and the caller hands them to UploadService.
 *
 * Rows whose extension differs from the existing media disable the Replace
 * choice (defence-in-depth — the backend rejects extension changes too).
 */
@Component({
	selector: 'app-upload-duplicate-dialog',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [Dialog, ButtonDirective, FormsModule],
	templateUrl: './upload-duplicate-dialog.html',
	styleUrl: './upload-duplicate-dialog.scss',
})
export class UploadDuplicateDialogComponent {
	visible = input<boolean>(false);
	duplicates = input<DuplicateRow[]>([]);
	nonDuplicates = input<File[]>([]);

	visibleChange = output<boolean>();
	confirmed = output<DuplicateResolution[]>();
	cancelled = output<void>();

	private readonly document = inject(DOCUMENT);
	protected readonly rows = signal<RowState[]>([]);

	constructor() {
		// Whenever the input list changes, seed each row's action. Default is
		// Replace — except when extensions don't match, where the only legal
		// option is "new".
		effect(() => {
			const dups = this.duplicates();
			this.rows.set(dups.map(d => {
				const matches = fileExtension(d.file.name) === fileExtension(d.existing.name);
				return {
					row: d,
					action: matches ? 'replace' : 'new',
					extensionMatches: matches,
				};
			}));
		});

		// Esc → cancel; Enter → confirm. Bound for the dialog's lifetime.
		effect(onCleanup => {
			if (!this.visible()) return;
			const handler = (event: KeyboardEvent): void => {
				if (event.key === 'Escape') {
					event.preventDefault();
					this.onCancel();
				} else if (event.key === 'Enter' && !event.isComposing) {
					event.preventDefault();
					this.onConfirm();
				}
			};
			this.document.addEventListener('keydown', handler);
			onCleanup(() => this.document.removeEventListener('keydown', handler));
		});
	}

	protected readonly duplicateCount = computed(() => this.rows().length);
	protected readonly nonDuplicateCount = computed(() => this.nonDuplicates().length);
	protected readonly totalCount = computed(() => this.duplicateCount() + this.nonDuplicateCount());

	protected formatSize(bytes: number): string {
		return formatBytes(bytes);
	}

	protected setRowAction(index: number, action: ResolutionAction): void {
		this.rows.update(rows => {
			if (!rows[index]) return rows;
			// Block illegal Replace on extension mismatch (defense-in-depth — the
			// radio is disabled, but a stray click shouldn't slip through).
			if (action === 'replace' && !rows[index].extensionMatches) return rows;
			const next = rows.slice();
			next[index] = { ...next[index], action };
			return next;
		});
	}

	protected applyAll(action: ResolutionAction): void {
		this.rows.update(rows => rows.map(r => {
			if (action === 'replace' && !r.extensionMatches) return r;
			return { ...r, action };
		}));
	}

	protected onConfirm(): void {
		const rows = this.rows();
		const resolutions: DuplicateResolution[] = rows.map(r => ({
			file: r.row.file,
			action: r.action,
			replaceMediaId: r.action === 'replace' ? r.row.existing.id : undefined,
		}));
		this.confirmed.emit(resolutions);
		this.visibleChange.emit(false);
	}

	protected onCancel(): void {
		this.cancelled.emit();
		this.visibleChange.emit(false);
	}

	protected onVisibleChange(value: boolean): void {
		// Treat dismiss-via-X / overlay-click as cancel, never as confirm.
		if (!value) this.cancelled.emit();
		this.visibleChange.emit(value);
	}
}
